'use client'

import { CheckmarkShield12 } from '@vinted/monochrome-icons'
import { Badge, Cell, Icon, Image, Spacer, Text } from '@vinted/web-ui'
import { noop } from 'lodash'
import { Fragment } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import {
  useConversationContext,
  useConversationSidebarContext,
} from '@marketplace-web/domain/core-conversation'
import { ItemPhotoDto, ItemThumbnailSize } from '@marketplace-web/domain/item-data'
import { useAbTest } from '@marketplace-web/shared/ab-tests'
import { FormattedRelativeTime, useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { ColorCode, colorCodeToHex, UserImage } from '@marketplace-web/shared/ui-helpers'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/domain/assets'
import { findThumbnail } from '@marketplace-web/shared/utils'
import {
  InboxListItemUserSide,
  InboxConversationListItemDto,
  CONVERSATION_URL,
} from '@marketplace-web/domain/conversation-messaging-data'

import { formatCurrencyAmount } from '_libs/utils/formatString'
import { transformCurrencyAmountDto } from 'data/transformers/currency-amount'
import { EscrowOrderTrackingEntrySource } from 'constants/escrow-order'

type Props = {
  conversation: InboxConversationListItemDto
  onClick?: () => void
}

const InboxListItem = ({ conversation, onClick = noop }: Props) => {
  const { conversation: activeConversation, getConversationRequest } = useConversationContext()
  const { closeSidebar } = useConversationSidebarContext()
  const { user } = useSession()
  const translate = useTranslate('conversations.list')
  const inboxTranslate = useTranslate('inbox')
  const navigate = useNavigate()
  const asset = useAsset('assets/no-photo')
  const pendingOfferVisibilityAbTest = useAbTest('inbox_pending_offer_visibility_v2')
  const pendingOfferDescriptionAbTest = useAbTest('inbox_pending_offer_description_v2')
  const { locale } = useIntl()

  const userId = user?.id

  const isConversationActive = conversation.id === activeConversation?.id
  const oppositeUser = conversation.opposite_user

  const { id } = conversation

  function renderPrefix() {
    return (
      <UserImage
        src={findThumbnail(oppositeUser?.photo) || asset(EMPTY_USER_IMAGE_NAME)}
        size={Image.Size.Large}
        styling={Image.Styling.Circle}
      />
    )
  }

  function renderBadge() {
    if (oppositeUser?.badge !== 'moderator') return null

    return (
      <>
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Icon
          name={CheckmarkShield12}
          color={Icon.Color.Primary}
          aria={{
            'aria-label': inboxTranslate('a11y.moderator_badge'),
          }}
          testId="moderator-badge-icon"
        />
      </>
    )
  }

  function renderUsername() {
    if (!oppositeUser?.login) return translate('deleted_opposite_user.login')

    return (
      <>
        {oppositeUser.login}
        {renderBadge()}
      </>
    )
  }

  function renderTitle() {
    return <Text as="h2" type={Text.Type.Title} text={renderUsername()} />
  }

  function renderLastInteractionTime() {
    const conversationUpdatedAt = conversation.updated_at

    if (!conversationUpdatedAt) return null

    const createdAt = new Date(conversationUpdatedAt).getTime()

    return (
      <Text as="h3" type={Text.Type.Subtitle} text={<FormattedRelativeTime value={createdAt} />} />
    )
  }

  function renderRemainingItemCount(itemCount: number, itemPhotosCount: number) {
    const remainingItemCount = itemCount - itemPhotosCount

    return remainingItemCount > 0 ? (
      <Image
        label={`+${remainingItemCount}`}
        size={Image.Size.Medium}
        styling={Image.Styling.Rounded}
        color={colorCodeToHex(ColorCode.GreyscaleLevel3)}
      />
    ) : null
  }

  const getPhotoThumbnail = (photo: ItemPhotoDto) => {
    const thumbnail = photo?.thumbnails?.find(thumb => thumb.type === ItemThumbnailSize.Medium)

    return thumbnail?.url
  }

  function renderOrderItems(
    itemPhotos: Array<ItemPhotoDto> | null | undefined,
    itemCount: number | null | undefined,
  ) {
    if (!itemPhotos || !itemCount) return null

    return (
      <div>
        {itemPhotos.map(photo => (
          <Fragment key={photo.id}>
            <Image
              src={getPhotoThumbnail(photo)}
              size={Image.Size.Medium}
              styling={Image.Styling.Rounded}
            />
            <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
          </Fragment>
        ))}
        <div data-testid="inbox-order-items" className="u-inline-block">
          {renderRemainingItemCount(itemCount, itemPhotos.length)}
        </div>
      </div>
    )
  }

  function renderMessageContent() {
    const { description, item_photos, item_count, current_user, pending_offer_request } =
      conversation

    const getLabel = () => {
      if (!pending_offer_request) return null
      if (current_user?.side !== InboxListItemUserSide.Seller) return null

      switch (pendingOfferVisibilityAbTest?.variant) {
        case 'a':
          return (
            <Text
              as="h3"
              type={Text.Type.Subtitle}
              text={translate('offer.pending_label')}
              theme="primary"
              bold
              testId="pending-offer-label"
            />
          )
        case 'b':
          return (
            <Badge
              content={translate('offer.pending_badge')}
              theme="primary"
              testId="pending-offer-badge"
            />
          )
        default:
          return null
      }
    }

    const getDescription = () => {
      if (!pending_offer_request?.is_last_message) return description
      if (current_user?.side !== InboxListItemUserSide.Seller) return description

      const transformedOfferPrice = transformCurrencyAmountDto(pending_offer_request.offered_price)
      const transformedLatestPrice = transformCurrencyAmountDto(
        pending_offer_request.original_offer_price,
      )

      switch (pendingOfferDescriptionAbTest?.variant) {
        case 'a':
          return translate('offer.buyer_description', {
            offer_price: formatCurrencyAmount(transformedOfferPrice, locale),
          })
        case 'b':
          return translate('offer.buyer_description_with_original_price', {
            offer_price: formatCurrencyAmount(transformedOfferPrice, locale),
            original_price: (
              <s key="original-price">{formatCurrencyAmount(transformedLatestPrice, locale)}</s>
            ),
          })
        default:
          return description
      }
    }

    return (
      <>
        {description && (
          <Text as="span" truncate text={getDescription()} testId="inbox-list-item-description" />
        )}
        <Cell
          styling={Cell.Styling.Tight}
          theme="transparent"
          body={
            <div className="u-flexbox u-align-items-flex-end u-justify-content-between">
              {renderOrderItems(item_photos, item_count)}
              {getLabel()}
            </div>
          }
        />
      </>
    )
  }

  const handleConversationClick = () => {
    closeSidebar()

    if (!userId || isConversationActive) return

    getConversationRequest({ conversationId: conversation.id })

    navigate(CONVERSATION_URL(conversation.id, EscrowOrderTrackingEntrySource.Inbox))

    onClick()
  }

  return (
    <div
      className={isConversationActive ? 'u-background-gray' : ''}
      data-testid={`inbox-list-item-${id}-container`}
    >
      <Cell
        title={renderTitle()}
        prefix={renderPrefix()}
        subtitle={renderLastInteractionTime()}
        body={renderMessageContent()}
        highlighted={conversation.unread}
        type={Cell.Type.Navigating}
        theme="transparent"
        onClick={handleConversationClick}
        testId={`inbox-list-item-${id}`}
      />
    </div>
  )
}

export default InboxListItem
